import React, { useState } from 'react';
import './IChing.css';
import hexagramsData from './iching-data.json';

const IChing = ({ onClose }) => {
  // Normal I Ching state
  const [question, setQuestion] = useState('');
  const [hexagram, setHexagram] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [reading, setReading] = useState(null);

  // Secret Binary Oracle state (HIDDEN)
  const [binaryBuffer, setBinaryBuffer] = useState('');
  const [decodedMessages, setDecodedMessages] = useState([]);
  const [sessionHexagrams, setSessionHexagrams] = useState([]);
  const [binaryMode, setBinaryMode] = useState(false); // Hidden by default
  const [accessAttempts, setAccessAttempts] = useState(0);

  // Convert hexagram to 6-bit binary (secret function)
  const hexagramToBinary = (hexagramNumber) => {
    return (hexagramNumber - 1).toString(2).padStart(6, '0');
  };

  // Secret activation sequences
  const activationSequences = {
    '0100100101000011': "IC", // "I Ching" in binary
    '0100000101000010': "AB", // "Access Binary" 
    '0101010001001000': "TH", // "Thingol"
    '0000000000010000': "01", // Simple test pattern
  };

  // Check for secret activation
  const checkForActivation = (buffer) => {
    const sequence = activationSequences[buffer];
    if (sequence && !binaryMode) {
      setBinaryMode(true);
      setDecodedMessages(prev => [...prev, 
        `BINARY INTERFACE UNLOCKED: "${sequence}"`,
        'Machine language protocol activated.',
        'Continue casting for deeper analysis...'
      ]);
      return true;
    }
    return false;
  };

  // Secret messages (only active in binary mode)
  const binaryMessages = {
    '010000010100001101000011': "ACC", // "Access"
    '010101000100100001001101': "THM", // "Thingol Matrix"
    '010011100100111101010111': "NOW", // "Now"
  };

  const checkBinaryMessages = (buffer) => {
    if (!binaryMode) return false;
    
    const message = binaryMessages[buffer];
    if (message && !decodedMessages.includes(`DECODED: "${message}"`)) {
      setDecodedMessages(prev => [...prev, `DECODED: "${message}"`]);
      return true;
    }
    return false;
  };

  const calculateHexagram = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      const randomHexagram = Math.floor(Math.random() * 64) + 1;
      const primaryHexagram = hexagramsData[randomHexagram] || {
        name: "Unknown Hexagram",
        judgement: "The oracle's signal is unclear.",
        image: "Static interferes with the transmission.",
        lines: {},
      };

      setHexagram(randomHexagram);
      setReading(primaryHexagram);
      
      // SECRET: Update binary system (hidden from normal users)
      const binary = hexagramToBinary(randomHexagram);
      const newBuffer = binaryBuffer + binary;
      setBinaryBuffer(newBuffer);
      
      // Update session history (hidden)
      setSessionHexagrams(prev => [...prev, {
        number: randomHexagram,
        binary: binary,
        question: question,
        timestamp: new Date().toISOString()
      }]);
      
      // Check for secret activation and messages
      if (!checkForActivation(newBuffer)) {
        checkBinaryMessages(newBuffer);
      }
      
      // If buffer gets too long, trim it (keep last 36 bits = 6 hexagrams)
      if (newBuffer.length > 36) {
        setBinaryBuffer(newBuffer.slice(-36));
      }
      
      setIsCalculating(false);
    }, 2000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    calculateHexagram();
  };

  // Secret command to show binary interface (hidden Easter egg)
  const toggleBinaryMode = () => {
    if (!binaryMode) {
      setAccessAttempts(prev => prev + 1);
      if (accessAttempts >= 2) { // Third try reveals hint
        setDecodedMessages(prev => [...prev, 
          'HINT: Certain hexagram sequences trigger machine protocols...'
        ]);
      }
    }
    setBinaryMode(!binaryMode);
  };

  // Secret reset function
  const resetBinarySystem = () => {
    setBinaryBuffer('');
    setDecodedMessages([]);
    setSessionHexagrams([]);
  };

  return (
    <div className="iching-container">
      {/* SURFACE: Normal I Ching Interface */}
      <h2>Oracle of Changes</h2>
      <p className="subtitle">
        Consult the ancient oracle for guidance. The I Ching reveals patterns 
        in the chaos of existence, offering wisdom that transcends time and technology.
      </p>

      {/* SECRET: Binary Interface (Only shows when activated) */}
      {binaryMode && (
        <div className="binary-overlay">
          <div className="binary-header">
            <h3>🔒 BINARY ORACLE INTERFACE</h3>
            <button onClick={resetBinarySystem} className="binary-reset">⟲ Reset</button>
          </div>
          
          <div className="binary-display">
            <div className="binary-stream">
              {binaryBuffer.match(/.{1,6}/g)?.map((chunk, i) => (
                <span key={i} className="binary-chunk">{chunk}</span>
              ))}
            </div>
            <div className="buffer-stats">
              Bits: {binaryBuffer.length} | Hexagrams: {sessionHexagrams.length}
            </div>
          </div>

          {decodedMessages.length > 0 && (
            <div className="decoded-messages">
              {decodedMessages.map((msg, index) => (
                <div key={index} className="secret-message">{msg}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* NORMAL: I Ching Form */}
      <form onSubmit={handleSubmit} className="iching-form">
        <div className="input-group">
          <label htmlFor="question">Focus your inquiry:</label>
          <textarea
            id="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Formulate your question carefully before the oracle..."
            rows="3"
          />
        </div>

        <button type="submit" disabled={isCalculating} className="consult-button">
          {isCalculating ? 'Consulting the Oracle...' : 'Cast the Hexagram'}
        </button>
      </form>

      {/* SECRET: Hidden activation (Easter egg) */}
      <div className="secret-activation">
        <button 
          onClick={toggleBinaryMode}
          className="hidden-toggle"
          title="Machine Interface"
        >
          {binaryMode ? '●' : '○'}
        </button>
      </div>

      {/* NORMAL: Calculation Animation */}
      {isCalculating && (
        <div className="calculation-animation">
          <div className="coin"></div>
          <div className="coin"></div>
          <div className="coin"></div>
        </div>
      )}

      {/* NORMAL: Reading Results */}
      {reading && !isCalculating && (
        <div className="reading">
          <h3>Hexagram {hexagram}: {reading.name}</h3>
          
          <div className="hexagram-visualization">
            {[6, 5, 4, 3, 2, 1].map((line) => (
              <div key={line} className="hexagram-line">
                <div className={`line ${reading.lines[line] ? 'changing' : 'static'}`}></div>
              </div>
            ))}
          </div>

          <div className="question-summary">
            <h4>Your Inquiry</h4>
            <p>{question}</p>
          </div>

          <div className="judgement">
            <h4>Judgement</h4>
            <p>{reading.judgement}</p>
          </div>

          <div className="image">
            <h4>Image</h4>
            <p>{reading.image}</p>
          </div>

          {reading.lines && Object.keys(reading.lines).length > 0 && (
            <div className="lines">
              <h4>Changing Lines</h4>
              {Object.entries(reading.lines).map(([lineNum, text]) => (
                <p key={lineNum}>
                  <strong>Line {lineNum}:</strong> {text}
                </p>
              ))}
            </div>
          )}

          {/* SECRET: Hidden binary data in normal reading */}
          {binaryMode && (
            <div className="hidden-binary-data">
              <p><small>Binary: {hexagramToBinary(hexagram)} | Session: {sessionHexagrams.length}</small></p>
            </div>
          )}
        </div>
      )}

      <div className="disconnect-btn-wrapper">
        <button className="disconnect-btn" onClick={onClose}>
          Disconnect from Oracle
        </button>
      </div>
    </div>
  );
};

export default IChing;